import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Target, ChevronUp, History, Volume2, FileVideo, HelpCircle, LogOut, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SetupPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [goal, setGoal] = useState(10);

  const handleStart = () => {
    navigate('/exercise', { state: { goal } });
  };

  const adjustGoal = (delta) => {
    setGoal(prev => Math.max(1, Math.min(100, prev + delta)));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      {/* Background texture */}
      <div className="fixed inset-0 carbon-texture pointer-events-none" />
      
      {/* Ambient glow */}
      <div className="fixed top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 p-4 sm:p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-gradient">
          HeelAI
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="tutorial-nav-button"
            onClick={() => navigate('/tutorial')}
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors px-3 py-2 rounded-lg hover:bg-cyan-500/10 border border-cyan-500/30 text-sm"
          >
            <HelpCircle size={18} />
            <span className="hidden sm:inline">How to Use</span>
          </button>
          <button
            data-testid="video-analysis-nav-button"
            onClick={() => navigate('/video-analysis')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-white/5 text-sm"
          >
            <FileVideo size={18} />
            <span className="hidden sm:inline">Analyze Video</span>
          </button>
          <button
            data-testid="history-nav-button"
            onClick={() => navigate('/history')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-white/5 text-sm"
          >
            <History size={18} />
            <span className="hidden sm:inline">History</span>
          </button>
          
          {/* User info and logout */}
          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-zinc-700">
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <User size={16} />
              <span className="hidden md:inline">{user?.name}</span>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors px-3 py-2 rounded-lg hover:bg-red-500/10 text-sm"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-8">
          {/* Exercise card */}
          <div className="glass rounded-2xl p-8 space-y-8">
            {/* Title */}
            <div className="text-center">
              <h2 className="font-heading text-4xl font-bold tracking-tight text-white mb-2">
                RIGHT LEG CALF RAISE
              </h2>
              <p className="text-zinc-400 text-sm">
                Single-leg calf raise with form tracking (side view)
              </p>
            </div>

            {/* Exercise illustration */}
            <div className="relative aspect-video rounded-xl overflow-hidden bg-zinc-900/50 border border-zinc-800">
              <img
                src="https://customer-assets.emergentagent.com/job_d50cdeb5-8adc-4a66-8754-6e99129c0aca/artifacts/avx0dqwz_image.png"
                alt="Heel raise exercise demonstration"
                className="w-full h-full object-cover opacity-70"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">How to perform</p>
                <p className="text-sm text-white">Stand sideways to camera on your right leg. Rise onto your toes, hold briefly, then lower. Keep knee straight.</p>
              </div>
            </div>

            {/* Goal setter */}
            <div className="space-y-4">
              <label className="block text-xs tracking-widest uppercase text-zinc-500 font-bold text-center">
                Set Your Rep Goal
              </label>
              <div className="flex items-center justify-center gap-6">
                <button
                  data-testid="goal-decrease-button"
                  onClick={() => adjustGoal(-5)}
                  className="w-14 h-14 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 flex items-center justify-center transition-colors"
                >
                  <ChevronUp className="rotate-180" size={24} />
                </button>
                
                <div className="text-center">
                  <input
                    data-testid="goal-input"
                    type="number"
                    value={goal}
                    onChange={(e) => setGoal(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                    className="w-24 font-data text-6xl font-bold text-cyan-400 bg-transparent text-center outline-none"
                    min="1"
                    max="100"
                  />
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Reps</p>
                </div>

                <button
                  data-testid="goal-increase-button"
                  onClick={() => adjustGoal(5)}
                  className="w-14 h-14 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 flex items-center justify-center transition-colors"
                >
                  <ChevronUp size={24} />
                </button>
              </div>
            </div>

            {/* Voice feedback indicator */}
            <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
              <Volume2 size={16} />
              <span>Voice feedback enabled</span>
            </div>

            {/* Start button */}
            <button
              data-testid="start-exercise-button"
              onClick={handleStart}
              className="w-full py-5 bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-lg rounded-full transition-transform active:scale-95 pulse-glow flex items-center justify-center gap-3"
            >
              <Play size={24} fill="currentColor" />
              START EXERCISE
            </button>
          </div>

          {/* Quick stats preview */}
          <div className="grid grid-cols-3 gap-4">
            <div className="stat-card text-center">
              <Target className="mx-auto mb-2 text-cyan-400" size={20} />
              <p className="text-xs text-zinc-500 uppercase">Goal</p>
              <p className="font-data text-xl text-white">{goal}</p>
            </div>
            <div className="stat-card text-center">
              <ChevronUp className="mx-auto mb-2 text-purple-400" size={20} />
              <p className="text-xs text-zinc-500 uppercase">Exercise</p>
              <p className="font-data text-xl text-white">Heel</p>
            </div>
            <div className="stat-card text-center">
              <Volume2 className="mx-auto mb-2 text-green-400" size={20} />
              <p className="text-xs text-zinc-500 uppercase">Voice</p>
              <p className="font-data text-xl text-white">On</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SetupPage;
